/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../../core/viewmodel/ViewModel';
import { StoreProvider } from '../../../../../stores/StoreProvider';
import * as H from 'history';
import { Message } from '../../../../../types/Message';
import { getChatImgUrl, getActiveRoomId } from '../../../../../utils/chat';
import { MaterialMenu } from '../../../../../models/material/MaterialMenu';
import { ChatStore } from '../../../../../stores/components/ChatStore';
import { socket } from '../../../../../config/web-sockets';
import { useParams } from 'react-router-dom';
import { AuthStore } from '../../../../../stores/application/AuthStore';
import { SnackbarStore } from '../../../../../stores/application/SnackbarStore';
import { role } from '../../../../../consts/chat';
import { EntriesReviewStore } from '../../../../../stores/components/EntriesReviewStore';
import { EntryService } from '../../../../../services/EntryService';

export class ChatMessageViewModel extends ViewModel {
	public menu: MaterialMenu = new MaterialMenu();
	private chatStore: ChatStore;
	private authStore: AuthStore;
	private snackBarStore: SnackbarStore;
	private entriesReviewStore: EntriesReviewStore;

	private entryService: EntryService;

	private routerParams: { courseId: string } = useParams();

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.chatStore = storeProvider.chatStore;
		this.authStore = storeProvider.authStore;
		this.snackBarStore = storeProvider.snackbarStore;
		this.entriesReviewStore = storeProvider.entriesReviewStore;
		this.entryService = storeProvider.entryService;
	}

	public getSenderImgUrl(message: Message): string {
		return getChatImgUrl(message);
	}

	public getTime(isoString: string): string {
		return new Date(isoString).toLocaleTimeString('de', {
			timeStyle: 'short',
			hour12: false,
			timeZone: 'Europe/Amsterdam',
		});
	}

	public setMessageToDelete(identifier: string): void {
		this.chatStore.setMessageToDelete(identifier);
	}

	public deleteMessage(): void {
		const userId = this.authStore.getUserId();
		const identifier = this.chatStore.getMessageToDelete();
		const room = getActiveRoomId(this.routerParams, this.chatStore.getActiveChat());
		socket.emit('deleteMessage', { userId, role: role, room, identifier }, (err: Error) => {
			if (err) {
				this.snackBarStore.showError('chat.delete_error');
			}
		});
	}

	public openEntryDetailView(entryId: string): void {
		const authToken = this.authStore.getAuthToken();

		this.entryService
			.getEntry(entryId, authToken)
			.then((entry) => {
				this.entriesReviewStore.setEntry(entry);
			})
			.catch(() => {
				this.snackBarStore.showError('chat.fetch_entry_error');
			});
	}

	public getWeekDayIndicator(isoString: string): number {
		const date = new Date(isoString);
		return date.getDay();
	}

	public getDateSeparator(isoString: string): string {
		const date = new Date(isoString);
		const dayOfMonth = date.getDate();
		const month = date.getMonth() + 1;
		return ' ' + (dayOfMonth < 10 ? '0' + dayOfMonth : dayOfMonth) + '.' + (month < 10 ? '0' + month : month) + '.';
	}
}
